'use client';

import Breadcrumb from '@/components/Breadcrumb';
import HeaderNavigator from '@/components/Navigator';
import React, { useEffect, useMemo } from 'react';
import routes from '@/app/routes';
import { SoftTagListWrapper } from './wrapper';
import { useLocation, useParams } from 'react-router-dom';
import { replacePlaceholders } from '@/utils/helpers';
import type {
  Material,
  MaterialComponent,
  MaterialRouting,
} from '../utils/match-maker.types';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  selectMaterialDetailData,
  selectMaterialDetailLoading,
} from '../store/material-detail.selectors';
import { getMaterialDetail } from '../store/material-detail.thunk';

interface SoftTagLocationState {
  selectedItem?: Material | MaterialComponent | MaterialRouting;
}

const SoftTagList: React.FC = () => {
  const { materialId, componentId } = useParams();
  const location = useLocation();
  const navigationState = location.state as SoftTagLocationState | null;

  const dispatch = useAppDispatch();
  const materialDetail = useAppSelector(selectMaterialDetailData);
  const isLoading = useAppSelector(selectMaterialDetailLoading);

  useEffect(() => {
    if (!navigationState?.selectedItem && materialId) {
      dispatch(getMaterialDetail({ id: materialId }));
    }
  }, [dispatch, materialId, navigationState]);

  const selectedItem = useMemo(() => {
    if (navigationState?.selectedItem) {
      return navigationState.selectedItem;
    } else if (materialDetail) {
      if (materialDetail.id === componentId) {
        return materialDetail;
      } else {
        const component = (materialDetail.components || []).find(
          comp => comp.id === componentId
        );
        if (component) {
          return component;
        }
        const routing = (materialDetail.routings || []).find(
          route => route.id === componentId
        );
        if (routing) {
          return routing;
        }
      }
    } else {
      return undefined;
    }
  }, [materialDetail, navigationState, componentId]);

  return (
    <>
      <HeaderNavigator title="Match item with soft tag" />
      <Breadcrumb
        items={[
          {
            label: 'Matchmaker',
            path: routes.MATCH_MAKER,
          },
          {
            label: 'Select produced material',
            path: routes.MATCH_MAKER_OUTPUT_MATERIALS,
          },
          {
            label: 'Select production & consumption item',
            path: replacePlaceholders(
              routes.MATCH_MAKER_PRODUCTION_CONSUMPTION,
              { materialId: materialId as string }
            ),
          },
          {
            label: 'Match item with soft tag',
          },
        ]}
        className="w-full fixed top-[112px]"
      />
      <SoftTagListWrapper selectedItem={selectedItem} isLoading={isLoading} />
    </>
  );
};

export default SoftTagList;
